import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PageHeader } from '@/components/Layout';
import { InfoTooltip } from '@/components/InfoTooltip';
import { DimensionInput } from '@/components/DimensionInput';
import { useAuth } from '@/context/AuthContext';
import { quotesApi, SavedQuote, freightApi } from '@/services/api';
import clsx from 'clsx';
import { ShippingDisclaimer } from '@/components/ShippingDisclaimer';

// Pricing matrices - these are MSRP prices
// Dealer price = MSRP * (1 - dealer_discount_percent / 100)
const PRICING_MATRICES = {
  cvw: {
    productType: 'cvw',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3988.6, 4111.96, 4239.13, 4370.24, 4505.4, 4644.74, 4788.4, 4936.49, 5089.17, 5246.56, 5408.83, 5576.11, 5748.57, 5926.36, 6109.65, 6298.61, 6493.41, 6694.24, 6901.27, 7114.72, 7334.76],
      96: [4107.4, 4234.43, 4365.39, 4500.41, 4639.59, 4783.09, 4931.05, 5083.62, 5240.94, 5403.15, 5570.39, 5742.81, 5920.56, 6103.77, 6291.62, 6486.21, 6686.81, 6893.62, 7106.83, 7326.63, 7553.22],
      108: [4230.6, 4361.44, 4496.33, 4635.4, 4778.76, 4926.55, 5078.92, 5235.99, 5397.99, 5565.06, 5737.35, 5914.99, 6098.14, 6286.93, 6480.34, 6680.76, 6887.38, 7100.39, 7319.99, 7546.39, 7779.78],
      120: [4358.2, 4492.99, 4631.95, 4775.2, 4922.89, 5075.15, 5232.13, 5394.01, 5560.99, 5733.23, 5910.87, 6094.06, 6282.96, 6477.71, 6677.35, 6882.99, 7095.11, 7314.55, 7540.77, 7773.99, 8014.43],
      132: [4489.1, 4627.94, 4771.07, 4918.63, 5070.75, 5227.58, 5389.27, 5555.98, 5727.85, 5905.05, 6087.73, 6276.03, 6470.12, 6670.01, 6876.3, 7088.97, 7308.22, 7534.25, 7767.26, 8007.49, 8255.14],
      144: [4623.3, 4766.29, 4913.7, 5065.67, 5222.34, 5383.86, 5550.37, 5722.02, 5898.96, 6081.35, 6269.33, 6463.05, 6662.66, 6868.29, 7081.87, 7300.89, 7526.7, 7759.48, 7999.46, 8246.87, 8501.93],
      156: [4761.9, 4909.18, 5061.01, 5217.53, 5378.9, 5545.26, 5716.75, 5893.54, 6075.77, 6263.59, 6457.13, 6656.55, 6862.01, 7073.64, 7294.17, 7519.76, 7752.33, 7992.1, 8239.28, 8494.1, 8756.8],
      168: [4904.9, 5056.6, 5212.99, 5374.21, 5540.43, 5711.78, 5888.41, 6070.5, 6258.16, 6451.55, 6650.8, 6856.07, 7067.51, 7285.25, 7513.22, 7745.58, 7985.14, 8232.1, 8486.7, 8749.18, 9019.77],
      180: [5052.3, 5208.56, 5369.65, 5535.72, 5706.93, 5883.43, 6065.38, 6252.92, 6446.19, 6645.35, 6850.54, 7061.9, 7279.58, 7503.7, 7739, 7978.35, 8225.1, 8479.49, 8741.74, 9012.1, 9290.83],
      192: [5204.1, 5365.05, 5530.98, 5702.04, 5878.39, 6060.2, 6247.61, 6440.78, 6639.85, 6845.06, 7056.52, 7274.39, 7498.81, 7729.93, 7971.52, 8218.07, 8472.23, 8734.26, 9004.39, 9282.88, 9569.98]
    }
  },
  solar99: {
    productType: 'solar99',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3438.44, 3544.78, 3654.41, 3767.43, 3884.00, 4003.07, 4127.91, 4255.58, 4387.19, 4522.89, 4662.77, 4806.97, 4955.64, 5108.91, 5266.92, 5430.81, 5599.75, 5773.87, 5953.35, 6138.36, 6329.05],
      96: [3546.01, 3666.71, 3790.80, 3918.40, 4049.59, 4184.50, 4323.25, 4465.96, 4612.72, 4763.70, 4919.00, 5078.75, 5243.12, 5412.23, 5586.23, 5765.26, 5949.49, 6139.09, 6334.21, 6535.01, 6741.70],
      108: [3617.67, 3729.56, 3844.90, 3963.82, 4086.41, 4212.80, 4343.09, 4477.40, 4615.85, 4758.58, 4905.71, 5057.36, 5213.67, 5374.80, 5540.47, 5712.85, 5890.53, 6074.69, 6263.47, 6458.07, 6658.64],
      120: [3725.73, 3840.90, 3959.55, 4082.40, 4208.40, 4338.60, 4473.00, 4611.60, 4754.40, 4901.40, 5052.60, 5209.05, 5369.70, 5536.65, 5707.80, 5884.20, 6065.85, 6253.80, 6447.00, 6646.50, 6852.30],
      132: [3796.49, 3913.91, 4034.95, 4159.74, 4288.40, 4421.03, 4557.77, 4698.77, 4844.14, 4994.04, 5148.59, 5307.94, 5472.23, 5641.58, 5815.37, 5995.23, 6180.64, 6371.80, 6568.87, 6772.02, 6981.47],
      144: [3959.34, 4081.79, 4208.03, 4338.18, 4472.35, 4610.68, 4753.30, 4900.34, 5051.95, 5208.26, 5369.41, 5535.52, 5706.75, 5883.23, 6064.83, 6252.40, 6446.77, 6646.12, 6851.65, 7063.52, 7280.95],
      156: [4345.11, 4479.50, 4617.04, 4758.86, 4905.11, 5055.90, 5211.38, 5371.68, 5536.95, 5707.34, 5883.00, 6064.06, 6250.70, 6443.09, 6641.74, 6846.60, 7058.80, 7277.59, 7502.13, 7733.65, 7970.35],
      168: [4457.15, 4595.00, 4736.11, 4880.61, 5028.65, 5180.37, 5335.89, 5495.38, 5658.99, 5826.89, 5999.21, 6176.14, 6357.86, 6544.54, 6736.36, 6933.51, 7136.19, 7344.61, 7559.00, 7779.49, 8006.38],
      180: [4529.07, 4669.20, 4815.67, 4964.61, 5118.15, 5276.45, 5439.64, 5607.87, 5781.31, 5960.12, 6144.45, 6334.48, 6529.39, 6730.37, 6936.63, 7148.25, 7365.54, 7588.68, 7817.88, 8053.34, 8295.32],
      192: [4644.26, 4787.90, 4935.97, 5088.62, 5246.01, 5408.25, 5575.52, 5747.96, 5925.74, 6109.01, 6297.94, 6492.73, 6693.53, 6900.55, 7113.97, 7333.98, 7559.81, 7792.65, 8031.73, 8277.25, 8530.46]
    }
  },
  solar94: {
    productType: 'solar94',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3274.70, 3375.98, 3480.39, 3588.03, 3699.00, 3813.40, 3931.34, 4052.93, 4178.28, 4307.51, 4440.73, 4578.07, 4719.66, 4865.63, 5016.11, 5171.25, 5331.19, 5496.07, 5666.05, 5841.29, 6021.95],
      96: [3378.10, 3482.58, 3590.29, 3701.33, 3815.80, 3933.81, 4055.48, 4180.91, 4310.21, 4443.52, 4580.95, 4722.62, 4868.69, 5019.26, 5174.50, 5334.53, 5499.52, 5669.61, 5844.96, 6025.73, 6212.09],
      108: [3445.40, 3551.96, 3661.81, 3775.07, 3891.82, 4012.19, 4136.27, 4264.19, 4396.05, 4531.98, 4672.10, 4816.53, 4965.40, 5118.86, 5277.59, 5440.81, 5609.08, 5782.56, 5961.40, 6145.78, 6335.85],
      120: [3548.60, 3658.00, 3771.00, 3888.00, 4008.00, 4132.00, 4260.00, 4392.00, 4528.00, 4668.00, 4812.00, 4961.00, 5114.00, 5273.00, 5436.00, 5604.00, 5777.00, 5956.00, 6140.00, 6330.00, 6526.00],
      132: [3615.70, 3727.53, 3842.81, 3961.66, 4084.19, 4210.50, 4340.74, 4475.02, 4613.47, 4756.23, 4903.42, 5055.18, 5211.64, 5372.94, 5538.45, 5709.74, 5886.33, 6068.38, 6256.06, 6449.55, 6649.02],
      144: [3770.80, 3887.42, 4007.65, 4131.60, 4259.38, 4391.12, 4526.95, 4666.99, 4811.38, 4960.25, 5113.72, 5271.92, 5435.00, 5603.08, 5776.03, 5954.67, 6138.83, 6328.69, 6524.43, 6726.21, 6934.24],
      156: [4138.20, 4266.19, 4398.13, 4534.15, 4674.39, 4818.95, 4967.98, 5121.60, 5279.95, 5443.18, 5611.42, 5784.82, 5963.53, 6147.71, 6338.80, 6534.85, 6736.96, 6945.32, 7160.12, 7381.57, 7609.86],
      168: [4244.90, 4376.19, 4511.53, 4651.06, 4794.91, 4943.21, 5096.09, 5253.70, 5416.19, 5583.70, 5756.39, 5934.42, 6117.96, 6307.18, 6502.24, 6703.34, 6910.66, 7124.39, 7344.74, 7571.89, 7806.08],
      180: [4315.30, 4448.76, 4586.35, 4728.20, 4874.43, 5025.19, 5180.61, 5340.83, 5506.01, 5676.30, 5851.86, 6032.84, 6219.42, 6411.78, 6610.08, 6814.52, 7025.27, 7242.55, 7466.55, 7697.47, 7935.54],
      192: [4423.10, 4559.90, 4700.92, 4846.31, 4996.20, 5150.72, 5310.02, 5474.25, 5643.56, 5818.10, 5998.04, 6183.55, 6374.79, 6571.95, 6775.21, 6984.75, 7200.77, 7423.48, 7653.07, 7889.76, 8133.77]
    }
  },
  insect: {
    productType: 'insect',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3158.10, 3255.77, 3356.47, 3460.28, 3567.29, 3677.62, 3791.36, 3908.62, 4029.51, 4154.13, 4282.61, 4415.06, 4551.61, 4692.38, 4837.51, 4987.12, 5141.36, 5300.37, 5464.30, 5633.30, 5807.53],
      96: [3256.00, 3356.70, 3460.52, 3567.54, 3677.88, 3791.63, 3908.89, 4029.79, 4154.42, 4282.91, 4415.37, 4551.93, 4692.71, 4837.84, 4987.47, 5141.72, 5300.74, 5464.68, 5633.69, 5807.93, 5987.56],
      108: [3317.60, 3420.21, 3525.99, 3635.04, 3747.46, 3863.36, 3982.85, 4106.03, 4233.02, 4363.94, 4498.90, 4638.04, 4781.49, 4929.37, 5081.83, 5238.99, 5401.03, 5568.07, 5740.28, 5917.81, 6100.84],
      120: [3414.40, 3520.00, 3628.87, 3741.10, 3856.80, 3976.09, 4099.06, 4225.83, 4356.53, 4491.27, 4630.17, 4773.37, 4921.00, 5073.20, 5230.10, 5391.86, 5558.62, 5730.53, 5907.76, 6090.48, 6278.84],
      132: [3474.90, 3582.37, 3693.17, 3807.39, 3925.14, 4046.54, 4171.69, 4300.71, 4433.72, 4570.85, 4712.21, 4857.95, 5008.20, 5163.09, 5322.77, 5487.40, 5657.11, 5832.07, 6012.44, 6198.40, 6390.10],
      144: [3624.50, 3736.60, 3852.16, 3971.30, 4094.13, 4220.75, 4351.29, 4485.86, 4624.60, 4767.63, 4915.08, 5067.09, 5223.81, 5385.37, 5551.93, 5723.64, 5900.66, 6083.15, 6271.29, 6465.25, 6665.20],
      156: [3998.60, 4122.27, 4249.76, 4381.20, 4516.70, 4656.39, 4800.40, 4948.87, 5101.93, 5259.72, 5422.39, 5590.09, 5762.98, 5941.22, 6124.97, 6314.40, 6509.69, 6711.02, 6918.58, 7132.55, 7353.15],
      168: [4089.80, 4216.29, 4346.69, 4481.12, 4619.71, 4762.59, 4909.89, 5061.74, 5218.29, 5379.68, 5546.06, 5717.59, 5894.42, 6076.72, 6264.66, 6458.42, 6658.16, 6864.08, 7076.38, 7295.23, 7520.86],
      180: [4154.70, 4283.20, 4415.67, 4552.23, 4693.02, 4838.17, 4987.80, 5142.06, 5301.10, 5465.05, 5634.07, 5808.32, 5987.96, 6173.15, 6364.08, 6560.90, 6763.82, 6973.01, 7188.67, 7410.99, 7640.20],
      192: [4255.90, 4387.53, 4523.22, 4663.12, 4807.34, 4956.02, 5109.30, 5267.31, 5430.22, 5598.17, 5771.31, 5949.80, 6133.81, 6323.52, 6519.09, 6720.71, 6928.57, 7142.86, 7363.77, 7591.52, 7826.30]
    }
  }
} as const;

// Accessories are priced from FIXED MSRP/dealer pairs and deliberately do NOT
// follow the dealer's discount rate — only screens do. So the effective spread
// here (28.2%) and on remotes (58.75%) differs from a dealer's headline rate,
// and two dealers on different rates see identical accessory pricing. Intended;
// confirmed by Toby 2026-08-24. "Estimated Profit" is therefore the spread
// between two price lists, not a rate calculation.
const UCHANNEL_RATES = { MSRP: 12.75, DEALER: 9.16 };

const REMOTE_PRICING: Record<string, Record<string, number>> = {
  MSRP: {
    'alpha_pioneer_1ch': 125, 'alpha_pioneer_4ch': 200, 'alpha_pioneer_8ch_lcd': 275, 'alpha_pioneer_16ch_lcd': 350,
    'keder_remote_15ch': 200,
  },
  DEALER: {
    'alpha_pioneer_1ch': 77, 'alpha_pioneer_4ch': 82.5, 'alpha_pioneer_8ch_lcd': 143, 'alpha_pioneer_16ch_lcd': 165,
    'keder_remote_15ch': 82.5,
  },
};

// Motor Package Options (matches NewOrderPage)
const MOTOR_PACKAGES = [
  { value: 'none', label: 'No Motor', description: 'Screen only - reusing existing motor', badge: null },
  { value: 'keder', label: 'Keder Screens', description: 'Keder motor with Keder remotes', badge: 'Default' },
  { value: 'alpha', label: 'Alpha Package', description: 'Alpha motor with Alpha remotes', badge: '+$125/unit' },
  { value: 'somfy', label: 'Somfy Package', description: 'Somfy motor with Somfy remotes', badge: null },
];

const ALPHA_REMOTES = [
  { value: 'none', label: 'None' },
  { value: 'alpha_pioneer_1ch', label: 'Alpha Pioneer 1-ch' },
  { value: 'alpha_pioneer_4ch', label: 'Alpha Pioneer 4-ch' },
  { value: 'alpha_pioneer_8ch_lcd', label: 'Alpha Pioneer 8-ch LCD' },
  { value: 'alpha_pioneer_16ch_lcd', label: 'Alpha Pioneer 16-ch LCD' },
];

const SOMFY_REMOTES = [
  { value: 'none', label: 'None' },
  { value: 'somfy_situo_1ch', label: 'Somfy Situo 1-ch' },
  { value: 'somfy_situo_5ch', label: 'Somfy Situo 5-ch' },
  { value: 'somfy_telis_1ch', label: 'Somfy Telis 1-ch' },
  { value: 'somfy_telis_4ch', label: 'Somfy Telis 4-ch' },
  { value: 'somfy_telis_16ch', label: 'Somfy Telis 16-ch' },
];

const KEDER_REMOTES = [
  { value: 'none', label: 'None' },
  { value: 'keder_remote_15ch', label: 'Keder 15-ch Remote' },
];

const ALPHA_WALL_SWITCHES = [
  { value: 'none', label: 'None' },
  { value: 'alpha_rts_wall_switch', label: 'Alpha RTS Wall Switch' },
];

const SOMFY_WALL_SWITCHES = [
  { value: 'none', label: 'None' },
  { value: 'somfy_rts_wall_switch', label: 'Somfy RTS Wall Switch' },
];

const KEDER_WALL_SWITCHES = [
  { value: 'none', label: 'None' },
  { value: 'keder_wall_switch', label: 'Keder Wall Switch' },
];

const WALL_SWITCH_PRICE = 82.5;
// Wall switch went $45 -> $82.50 on this date. A saved quote created before it
// (and still within its 30-day validity) is honored at the old $45 price.
const WALL_SWITCH_OLD_PRICE = 45;
const WALL_SWITCH_PRICE_CHANGE = new Date('2026-07-16T00:00:00');
function wallSwitchPriceForQuote(loadedQuoteCreatedAt: string | null): number {
  if (loadedQuoteCreatedAt) {
    const created = new Date(loadedQuoteCreatedAt);
    const ageDays = (Date.now() - created.getTime()) / 86_400_000;
    if (created < WALL_SWITCH_PRICE_CHANGE && ageDays <= 30) return WALL_SWITCH_OLD_PRICE;
  }
  return WALL_SWITCH_PRICE;
}
const SOMFY_UPGRADE_PER_UNIT = 275;
const ALPHA_UPGRADE_PER_UNIT = 125;

const PRODUCT_LABELS_SHORT: Record<string, string> = {
  solar94: 'Solar',
  solar99: 'Premium Solar',
  cvw: 'Clear Vinyl Window',
  insect: 'Insect',
};

const PRODUCT_LABELS: Record<string, string> = {
  solar94: 'Solar — Omega Pro 3%/5%/10%, Twitchell 80/90/95',
  solar99: 'Premium Solar — Nano 95/97/99, Dimout',
  cvw: 'Clear Vinyl Window — 502 Vinyl (1 window included)',
  insect: 'Insect — Twitchell Nano 55',
};

const MIN_WIDTH = 48;   // smallest pricing bucket — dims below this quote at the 48x84 minimum
const MIN_HEIGHT = 84;
const MAX_WIDTH = 288;
const MAX_HEIGHT = 192;
// Hard manufacturing floor: we don't build screens narrower than 30". Distinct
// from MIN_WIDTH (the 48" min pricing bucket) — 30–48" still prices at the 48"
// bucket, but below 30" is not buildable at all. Mirrors NewOrderPage's rule.
const MIN_BUILD_WIDTH = 30;

interface ScreenItem {
  id: number;
  width: number;
  height: number;
  uChannel: boolean;
  windowPanel?: boolean;     // include vinyl window panel on this screen
  windowCount?: number;      // first included; +$175 each after (matches NewOrder)
}

// Fabric type catalog: dealer picks the actual fabric (Omega Pro 5%, Twitchell Nano 99,
// 502 Vinyl, etc.) — the pricingCategory drives which matrix is used for MSRP lookup.
// Mirrors NewOrderPage's FALLBACK_FABRIC_TYPES so the same selections are available.
type FabricOption = { value: string; label: string; pricingCategory: keyof typeof PRICING_MATRICES };
const FABRIC_TYPE_GROUPS: { group: string; options: FabricOption[] }[] = [
  { group: 'Solar', options: [
    { value: 'omega_pro_3', label: 'Omega Pro 3%', pricingCategory: 'solar94' },
    { value: 'omega_pro_5', label: 'Omega Pro 5%', pricingCategory: 'solar94' },
    { value: 'omega_pro_10', label: 'Omega Pro 10%', pricingCategory: 'solar94' },
    { value: 'twitchell_80', label: 'Twitchell 80', pricingCategory: 'solar94' },
    { value: 'twitchell_90', label: 'Twitchell 90', pricingCategory: 'solar94' },
    { value: 'twitchell_95', label: 'Twitchell 95', pricingCategory: 'solar94' },
  ]},
  { group: 'Solar Premium', options: [
    { value: 'twitchell_nano_95', label: 'Twitchell Nano 95', pricingCategory: 'solar99' },
    { value: 'twitchell_nano_97', label: 'Twitchell Nano 97', pricingCategory: 'solar99' },
    { value: 'twitchell_nano_99', label: 'Twitchell Nano 99', pricingCategory: 'solar99' },
    { value: 'twitchell_dimout_blackout', label: 'Twitchell Dimout', pricingCategory: 'solar99' },
  ]},
  { group: 'Vinyl', options: [
    { value: '502_vinyl', label: '502 Vinyl', pricingCategory: 'cvw' },
  ]},
  { group: 'Insect', options: [
    { value: 'twitchell_nano_55_insect', label: 'Twitchell Nano 55 Insect', pricingCategory: 'insect' },
  ]},
];
const FABRIC_FLAT: FabricOption[] = FABRIC_TYPE_GROUPS.flatMap(g => g.options);

// Fabric colors per fabric type (cosmetic only)
const COLORS_BY_FABRIC: Record<string, string[]> = {
  omega_pro_3: ['Brown/Taupe', 'White', 'White/Linen', 'White Gray', 'Light Gray', 'Black/Bronze', 'Gray/Black', 'Black', 'Charcoal/Taupe'],
  omega_pro_5: ['Brown/Taupe', 'White', 'White/Linen', 'White Gray', 'Light Gray', 'Black/Bronze', 'Gray/Black', 'Black', 'Charcoal/Taupe'],
  omega_pro_10: ['Brown/Taupe', 'White', 'White/Linen', 'White Gray', 'Light Gray', 'Black/Bronze', 'Gray/Black', 'Black', 'Charcoal/Taupe'],
  twitchell_80: ['Black'],
  twitchell_90: ['Black'],
  twitchell_95: ['Black'],
  twitchell_nano_95: ['Black'],
  twitchell_nano_97: ['Black'],
  twitchell_nano_99: ['Black'],
  twitchell_dimout_blackout: ['Black'],
  '502_vinyl': ['Black', 'Red (Poppy)', 'White', 'Blue (Midnight)', 'Anthracite', 'Grey (Concrete)', 'Boulder', 'Antelope', 'Tan (Pepper)', 'Hemp'],
  twitchell_nano_55_insect: ['Standard'],
};

const EXTRA_WINDOW_PRICE = 175; // first window included; +$175 each after (matches NewOrder)

/**
 * Get MSRP price for a screen based on dimensions
 * - Screens smaller than minimum (48" W or 84" H) use the minimum bucket price
 * - Screens larger than maximum require custom quote (returns null)
 * - Uses "round UP" bucketing for values between buckets
 */
function getScreenMSRP(productType: keyof typeof PRICING_MATRICES, width: number, height: number): number | null {
  const matrix = PRICING_MATRICES[productType];
  if (!matrix) return null;

  // Check upper bounds - exceeding max requires custom quote
  if (width > MAX_WIDTH || height > MAX_HEIGHT) return null;
  // Minimum orderable width is 30" (matches the Min Width manufacturing rule) — no quote below.
  if (width > 0 && width < 30) return null;

  // For dimensions below minimum, use the minimum bucket
  const effectiveWidth = Math.max(width, MIN_WIDTH);
  const effectiveHeight = Math.max(height, MIN_HEIGHT);

  // Find the next bucket >= value (round UP)
  const heightBucket = matrix.heights.find(h => h >= effectiveHeight) || matrix.heights[matrix.heights.length - 1];
  const widthBucket = matrix.widths.find(w => w >= effectiveWidth) || matrix.widths[matrix.widths.length - 1];

  const widthIdx = matrix.widths.indexOf(widthBucket);
  return matrix.prices[heightBucket as keyof typeof matrix.prices]?.[widthIdx] ?? null;
}

function getRemotePrice(remoteType: string, qty: number, isDealer: boolean): number {
  if (qty <= 0 || remoteType === 'none') return 0;
  const prices = isDealer ? REMOTE_PRICING.DEALER : REMOTE_PRICING.MSRP;
  const unitPrice = prices[remoteType] ?? prices['alpha_pioneer_1ch'];
  return unitPrice * qty;
}

function calculatePackaging(prePackSubtotal: number, qty: number, isFreight: boolean): number {
  // Packaging only applies to freight shipments, not will call. Full rate;
  // large-order packaging discount removed 2026-09-18.
  if (!isFreight) return 0;
  return 82.5 * qty;
}

export function QuoteCalculatorPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [productType, setProductType] = useState<keyof typeof PRICING_MATRICES>('solar94');
  const [fabricType, setFabricType] = useState<string>('omega_pro_5');
  const [fabricColor, setFabricColor] = useState<string>('Black');
  const [pricingMode, setPricingMode] = useState<'dealer' | 'msrp'>('dealer');
  const [pricingDisplay, setPricingDisplay] = useState<'both' | 'dealer_only' | 'msrp_only'>('both');
  const [screens, setScreens] = useState<ScreenItem[]>([]);
  const [motorPackage, setMotorPackage] = useState<'alpha' | 'somfy' | 'keder' | 'none'>('keder');
  const [remoteType, setRemoteType] = useState('none');
  const [remoteQty, setRemoteQty] = useState(0);
  const [wallSwitchType, setWallSwitchType] = useState('none');
  const [wallSwitchQty, setWallSwitchQty] = useState(0);
  const [msrpMarkup, setMsrpMarkup] = useState(0);
  const [msrpDiscount, setMsrpDiscount] = useState(0);
  const [salesTax, setSalesTax] = useState(0);
  const [installationCost, setInstallationCost] = useState(0);
  const [editingQuoteId, setEditingQuoteId] = useState<string | null>(null);
  // createdAt of the loaded saved quote — honors $45 wall switch within its 30-day validity.
  const [loadedQuoteCreatedAt, setLoadedQuoteCreatedAt] = useState<string | null>(null);
  const [priceMultiplier, setPriceMultiplier] = useState(1);

  // Fetch price increase + tariff + firm adjustment on mount so quote matches New Order
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    fetch(`${import.meta.env.VITE_API_URL || '/api'}/system-settings/pricing`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(res => res.json())
      .then(data => {
        const pi = data.price_increase_percent || 0;
        const tariff = data.tariff_percent || 0;
        const firmAdj = data.firm_price_adjustment_percent || 0;
        setPriceMultiplier((1 + pi / 100) * (1 + tariff / 100) * (1 + firmAdj / 100));
      })
      .catch(err => console.error('Failed to fetch pricing settings:', err));
  }, []);

  // Saved quotes state
  const [savedQuotes, setSavedQuotes] = useState<SavedQuote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [quoteName, setQuoteName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Email estimate modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailName, setEmailName] = useState('');
  const [emailNotes, setEmailNotes] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailPreviewImages, setEmailPreviewImages] = useState<string[]>([]);
  const emailImageRef = useRef<HTMLInputElement>(null);
  const [emailDesigns, setEmailDesigns] = useState<any[]>([]);
  const [showDesignPicker, setShowDesignPicker] = useState(false);

  // Fraction input mode
  const [fractionMode, setFractionMode] = useState(() => {
    return localStorage.getItem('fractionMode') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('fractionMode', fractionMode ? 'true' : 'false');
  }, [fractionMode]);

  // Shipping state
  const [destinationZip, setDestinationZip] = useState('');
  const [liftgate, setLiftgate] = useState(false);
  const [residential, setResidential] = useState(false);
  const [freightLoading, setFreightLoading] = useState(false);
  const [freightInfo, setFreightInfo] = useState<{
    carrier: string;
    service: string;
    transitTime: string;
    rate: number;
    fallback?: boolean;
  } | null>(null);
  const [freightError, setFreightError] = useState('');
  const [taxPreview, setTaxPreview] = useState(0);
  const [taxRatePct, setTaxRatePct] = useState(0);
  const [taxState, setTaxState] = useState<string | null>(null);
  const [taxStateSource, setTaxStateSource] = useState<'explicit' | 'dealer_home' | null>(null);

  // Get dealer discount from user profile (this is applied internally, never shown to dealers)
  const dealerDiscount = useMemo(() => {
    const u = user as any;
    return u?.discountPercent || u?.discount_percent || u?.discountRate || 0;
  }, [user]);

  // Auto-load first screen on mount
  useEffect(() => {
    if (screens.length === 0) {
      setScreens([{ id: 1, width: 0, height: 0, uChannel: false, windowPanel: false, windowCount: 1 }]);
    }
  }, []);

  // Load saved quotes on mount
  useEffect(() => {
    loadSavedQuotes();
  }, []);

  // Load dealer quote defaults when user is available
  const dealerId = (user as any)?.dealerId;
  useEffect(() => {
    if (!dealerId) return;
    const token = localStorage.getItem('auth_token');
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    fetch(`${apiUrl}/dealers/${dealerId}/quote-defaults`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : null).then(defaults => {
      if (defaults) {
        if (defaults.salesTax > 0) setSalesTax(defaults.salesTax);
        if (defaults.installationCost > 0) setInstallationCost(defaults.installationCost);
        if (defaults.markup > 0) setMsrpMarkup(defaults.markup);
        if (defaults.pricingDisplay) {
          setPricingDisplay(defaults.pricingDisplay);
          if (defaults.pricingDisplay === 'msrp_only') setPricingMode('msrp');
          if (defaults.pricingDisplay === 'dealer_only') setPricingMode('dealer');
        }
      }
    }).catch(() => {});
  }, [dealerId]);

  const loadSavedQuotes = async () => {
    try {
      setLoadingQuotes(true);
      const response = await quotesApi.list(1, 50);
      setSavedQuotes(response.data);
    } catch (error) {
      console.error('Failed to load saved quotes:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const addScreen = () => {
    if (screens.length >= 100) return;
    const maxId = Math.max(...screens.map(s => s.id), 0);
    setScreens([...screens, { id: maxId + 1, width: 0, height: 0, uChannel: false, windowPanel: false, windowCount: 1 }]);
  };

  const removeScreen = (id: number) => {
    // Always keep at least one screen
    if (screens.length <= 1) return;
    setScreens(screens.filter(s => s.id !== id));
  };

  const updateScreen = (id: number, updates: Partial<ScreenItem>) => {
    setScreens(screens.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const duplicateScreen = (id: number) => {
    if (screens.length >= 100) return;
    const screen = screens.find(s => s.id === id);
    if (!screen) return;
    const maxId = Math.max(...screens.map(s => s.id), 0);
    setScreens([...screens, { ...screen, id: maxId + 1 }]);
  };

  const quote = useMemo(() => {
    const qty = screens.length;
    let screenTotalMSRP = 0;
    let uChannelFeetTotal = 0;
    let extraWindowsMSRPTotal = 0;
    let extraWindowsCount = 0;

    const lineItems = screens.map((screen, i) => {
      // Business rule: a screen with a vinyl window panel is priced using the CVW
      // matrix, regardless of fabric. (Solar + window = CVW base price.)
      const effectiveCategory: keyof typeof PRICING_MATRICES = screen.windowPanel ? 'cvw' : productType;
      // Below the 30" buildable floor there is no valid product — don't price it,
      // don't contribute it to totals, and flag it so the UI blocks the quote
      // (mirrors NewOrderPage, which refuses to submit a sub-30" screen).
      const isBelowMinWidth = screen.width > 0 && screen.width < MIN_BUILD_WIDTH;
      const rawMsrp = isBelowMinWidth ? 0 : (getScreenMSRP(effectiveCategory, screen.width, screen.height) || 0);
      const msrpPrice = rawMsrp * priceMultiplier;
      screenTotalMSRP += msrpPrice;

      // Dealer price = MSRP * (1 - discount/100) - applied internally
      const dealerPrice = msrpPrice * (1 - dealerDiscount / 100);

      if (screen.uChannel && !isBelowMinWidth) {
        // DELIBERATE, NOT A BUG: quotes both sides regardless of how many the
        // dealer will actually order. This is an estimate — the real side count
        // (none / one_side / two_sides) is set at order entry and trued up then,
        // so quoting high here is intentional padding rather than an oversight.
        //
        // Reads like an oversight because the order form DOES distinguish sides:
        // VT26070005 is "One Side" on screens 1 and 6 and bills 17.75 ft
        // ($162.59), where this quotes 35.5 ft ($325.18). Confirmed intended by
        // Toby 2026-08-24 — do not "fix" it to match the order form.
        uChannelFeetTotal += (Math.max(screen.height, MIN_HEIGHT) / 12) * 2;
      }

      // Window panels: first window included; +$175 per additional (flat — no dealer
      // discount applied, matching NewOrderPage which adds $175 raw to dealer total).
      const winCount = screen.windowPanel && !isBelowMinWidth ? Math.max(1, screen.windowCount || 1) : 0;
      const extraWindows = winCount > 0 ? Math.max(0, winCount - 1) : 0;
      const extraWindowCost = extraWindows * EXTRA_WINDOW_PRICE;
      const extraWindowMSRP = extraWindowCost;
      const extraWindowDealer = extraWindowCost;
      extraWindowsMSRPTotal += extraWindowCost;
      extraWindowsCount += extraWindows;

      // Check if screen has valid dimensions entered
      const hasValidDimensions = screen.width > 0 && screen.height > 0;
      const isUnderMinimum = screen.width < MIN_WIDTH || screen.height < MIN_HEIGHT;
      const isOverMaximum = screen.width > MAX_WIDTH || screen.height > MAX_HEIGHT;

      return {
        number: i + 1,
        width: screen.width,
        height: screen.height,
        msrpPrice,
        dealerPrice,
        hasUChannel: screen.uChannel,
        windowCount: winCount,
        extraWindowMSRP,
        extraWindowDealer,
        hasValidDimensions,
        isUnderMinimum,
        isOverMaximum,
        isBelowMinWidth,
      };
    });

    // Extras are flat-priced — same value for both dealer + MSRP views
    const extraWindowsDealerTotal = extraWindowsMSRPTotal;

    // Remotes
    const remoteMSRP = qty > 0 ? getRemotePrice(remoteType, remoteQty, false) : 0;
    const remoteDealer = qty > 0 ? getRemotePrice(remoteType, remoteQty, true) : 0;

    // Wall switches
    const wallSwitchTotal = wallSwitchQty > 0 ? wallSwitchPriceForQuote(loadedQuoteCreatedAt) * wallSwitchQty : 0;

    // Somfy upgrade ($275/unit)
    const somfyUpgrade = motorPackage === 'somfy' ? SOMFY_UPGRADE_PER_UNIT * qty : 0;
    // Alpha upgrade ($125/unit) — Keder is the default
    const alphaUpgrade = motorPackage === 'alpha' ? ALPHA_UPGRADE_PER_UNIT * qty : 0;
    const motorUpgrade = somfyUpgrade + alphaUpgrade;

    // U-Channel
    const uChannelMSRP = uChannelFeetTotal * UCHANNEL_RATES.MSRP;
    const uChannelDealer = uChannelFeetTotal * UCHANNEL_RATES.DEALER;

    // Apply dealer discount to screen subtotal to get dealer screen price
    const screenDealerTotal = screenTotalMSRP * (1 - dealerDiscount / 100);

    // Dealer subtotal (screens at dealer price + accessories at dealer price)
    const subtotalDealer = screenDealerTotal + uChannelDealer + extraWindowsDealerTotal + remoteDealer + wallSwitchTotal + motorUpgrade;

    // MSRP subtotal (for customer-facing quotes)
    const subtotalMSRP = screenTotalMSRP + uChannelMSRP + extraWindowsMSRPTotal + remoteMSRP + wallSwitchTotal + motorUpgrade;

    // MSRP adjustments (for customer-facing quotes)
    const msrpMarkupAmount = subtotalMSRP * (msrpMarkup / 100);
    const markedUpMSRP = subtotalMSRP + msrpMarkupAmount;
    const msrpDiscountAmount = markedUpMSRP * (msrpDiscount / 100);
    const discountedMSRPProduct = markedUpMSRP - msrpDiscountAmount;

    // Packaging (only for freight, not will call)
    const packaging = calculatePackaging(subtotalDealer, qty, !!freightInfo);

    // Sales tax on MSRP only
    const salesTaxAmount = discountedMSRPProduct * (salesTax / 100);

    // Installation
    const installationTotal = installationCost * qty;

    // Customer-facing display prices.
    //
    // The totals math above is correct and unchanged. What was wrong was the
    // itemisation: msrpMarkup was folded into every downstream figure but never
    // shown, so a "10% discount" was computed against a number nobody could see,
    // and the emailed lines could not be reconciled to the total.
    //
    // Neither markup is ever itemised. priceMultiplier (the system price
    // increase and tariff) is already baked into msrpPrice at line-item level;
    // msrpMarkup is baked in here. Both are inflation-type adjustments that move
    // over time and are nobody's business but ours — dealer or customer.
    const displayMarkup = 1 + (msrpMarkup / 100);
    const displayScreenTotal = screenTotalMSRP * displayMarkup;

    // Grand totals
    const grandMSRP = discountedMSRPProduct + packaging + salesTaxAmount + installationTotal;
    const grandDealer = subtotalDealer + packaging + salesTaxAmount + installationTotal;
    const profit = grandMSRP - grandDealer;

    return {
      lineItems,
      screensMSRP: screenTotalMSRP,
      screensDealer: screenDealerTotal,
      remoteMSRP,
      remoteDealer,
      wallSwitchTotal,
      somfyUpgrade,
      alphaUpgrade,
      uChannelMSRP,
      uChannelDealer,
      uChannelFeet: uChannelFeetTotal,
      extraWindowsMSRP: extraWindowsMSRPTotal,
      extraWindowsDealer: extraWindowsDealerTotal,
      extraWindowsCount,
      subtotalMSRP,
      subtotalDealer,
      displayMarkup,
      displayScreenTotal,
      markedUpMSRP,
      msrpMarkupAmount,
      msrpDiscountAmount,
      packaging,
      salesTaxAmount,
      installationTotal,
      grandTotalMSRP: grandMSRP,
      grandTotalDealer: grandDealer,
      profit,
    };
  }, [screens, productType, motorPackage, remoteType, remoteQty, wallSwitchQty, dealerDiscount, msrpMarkup, msrpDiscount, salesTax, installationCost, freightInfo, priceMultiplier, loadedQuoteCreatedAt]);

  // Live tax preview. Taxable base = goods + packaging + freight.
  // EXCLUDES installationTotal (TX exempts separately-stated install labor)
  // and the user's manual salesTaxAmount field (we don't double-tax).
  useEffect(() => {
    const goods = pricingMode === 'msrp' ? quote.subtotalMSRP : quote.subtotalDealer;
    const taxableBase = (goods || 0) + (quote.packaging || 0) + (freightInfo?.rate || 0);
    if (taxableBase <= 0) { setTaxPreview(0); setTaxState(null); return; }
    const params = new URLSearchParams({ taxableAmount: String(taxableBase) });
    if (destinationZip && destinationZip.length >= 3) params.append('shipToZip', destinationZip);
    const token = localStorage.getItem('auth_token') || localStorage.getItem('admin_auth_token');
    fetch(`${import.meta.env.VITE_API_URL || '/api'}/tax/preview?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : { tax: 0 })
      .then(d => {
        setTaxPreview(parseFloat(d.tax) || 0);
        setTaxRatePct(parseFloat(d.ratePercent) || 0);
        setTaxState(d.shipToState || null);
        setTaxStateSource(d.stateSource || null);
      })
      .catch(() => { setTaxPreview(0); setTaxRatePct(0); setTaxState(null); setTaxStateSource(null); });
  }, [quote.grandTotalMSRP, quote.grandTotalDealer, pricingMode, destinationZip, freightInfo?.rate]);

  const hasValidScreens = screens.some(s => s.width > 0 && s.height > 0 && s.width <= MAX_WIDTH && s.height <= MAX_HEIGHT);
  // Screens entered below the 30" buildable floor — block the estimate, like New Order.
  const belowMinWidthNumbers = screens
    .map((s, i) => (s.width > 0 && s.width < MIN_BUILD_WIDTH ? i + 1 : 0))
    .filter(Boolean);
  const belowMinWidthError = belowMinWidthNumbers.length
    ? `Screen #${belowMinWidthNumbers[0]} width must be at least 30". We don't build screens narrower than 30 inches.`
    : null;

  const handleSaveQuote = async (forceNew = false) => {
    if (!quoteName.trim()) {
      setSaveError('Please enter a name for this estimate');
      return;
    }
    if (belowMinWidthError) {
      setSaveError(belowMinWidthError);
      return;
    }
    if (!hasValidScreens) {
      setSaveError('Please add at least one valid screen with dimensions');
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      const quoteData = {
        name: quoteName.trim(),
        configuration: {
          productType,
          fabricType,
          fabricColor,
          pricingMode,
          screens: screens.map(s => ({
            width: s.width,
            height: s.height,
            uChannel: s.uChannel,
            windowPanel: s.windowPanel || false,
            windowCount: s.windowCount || 1,
          })),
          motorPackage,
          remoteType,
          remoteQty,
          wallSwitchType,
          wallSwitchQty,
          extraRemote: remoteQty > 1,
          dealerDiscount,
          msrpMarkup,
          msrpDiscount,
          salesTax,
          installationCost,
          // Freight was displayed on screen (summary line + grand total) but
          // never persisted, so saving an estimate silently dropped the
          // shipping quote and the ZIP it was quoted to. Reloading showed a
          // total with no shipping in it.
          destinationZip,
          liftgate,
          residential,
          freightInfo,
        },
        pricing: {
          screensMSRP: quote.screensMSRP,
          screensDealer: quote.screensDealer,
          remoteMSRP: quote.remoteMSRP,
          remoteDealer: quote.remoteDealer,
          wallSwitchTotal: quote.wallSwitchTotal,
          somfyUpgrade: quote.somfyUpgrade,
          alphaUpgrade: quote.alphaUpgrade,
          uChannelMSRP: quote.uChannelMSRP,
          uChannelDealer: quote.uChannelDealer,
          uChannelFeet: quote.uChannelFeet,
          packaging: quote.packaging,
          salesTaxAmount: quote.salesTaxAmount,
          installationTotal: quote.installationTotal,
          grandTotalMSRP: quote.grandTotalMSRP,
          grandTotalDealer: quote.grandTotalDealer,
          // Freight is quoted separately from goods, so grandTotal* stay
          // product-only (the on-screen total adds freight on top — including
          // it here would double-count). Stored alongside so anything reading
          // a saved estimate can see the shipping and the all-in figure.
          freightRate: freightInfo?.rate ?? 0,
          freightCarrier: freightInfo?.carrier ?? null,
          freightService: freightInfo?.service ?? null,
          freightTransitTime: freightInfo?.transitTime ?? null,
          grandTotalWithFreightMSRP: quote.grandTotalMSRP + (freightInfo?.rate || 0),
          grandTotalWithFreightDealer: quote.grandTotalDealer + (freightInfo?.rate || 0),
        },
      };

      if (editingQuoteId && !forceNew) {
        await quotesApi.update(editingQuoteId, quoteData);
      } else {
        await quotesApi.create(quoteData);
      }

      setShowSaveModal(false);
      setQuoteName('');
      setEditingQuoteId(null);
      setLoadedQuoteCreatedAt(null);
      await loadSavedQuotes();
    } catch (error: any) {
      setSaveError(error.response?.data?.message || 'Failed to save estimate');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuote = async (id: string) => {
    if (!confirm('Are you sure you want to delete this estimate?')) return;

    try {
      await quotesApi.delete(id);
      await loadSavedQuotes();
    } catch (error) {
      console.error('Failed to delete estimate:', error);
    }
  };

  const handleLoadQuote = (savedQuote: SavedQuote) => {
    // If this was saved from Price Builder with extended data, redirect there
    const cfg = savedQuote.configuration as any;
    if (cfg.customerInfo || cfg.customItems?.length || cfg.discountsConfig?.length) {
      sessionStorage.setItem('loadQuoteId', savedQuote.id);
      navigate('/price-builder');
      return;
    }
    setProductType(savedQuote.configuration.productType as keyof typeof PRICING_MATRICES);
    if (cfg.fabricType) setFabricType(cfg.fabricType as string);
    setFabricColor((cfg.fabricColor as string) || 'Black');
    setScreens(savedQuote.configuration.screens.map((s: any, i: number) => ({
      id: i + 1,
      width: s.width,
      height: s.height,
      uChannel: s.uChannel,
      windowPanel: s.windowPanel || false,
      windowCount: s.windowCount || 1,
    })));
    setMotorPackage(cfg.motorPackage || 'keder');
    setRemoteType(cfg.remoteType || 'none');
    setRemoteQty(cfg.remoteQty ?? (cfg.extraRemote ? 2 : 0));
    setWallSwitchType(cfg.wallSwitchType || 'none');
    setWallSwitchQty(cfg.wallSwitchQty || 0);
    if (cfg.pricingMode) setPricingMode(cfg.pricingMode);
    if (cfg.msrpMarkup != null) setMsrpMarkup(cfg.msrpMarkup);
    if (cfg.msrpDiscount != null) setMsrpDiscount(cfg.msrpDiscount);
    if (cfg.salesTax != null) setSalesTax(cfg.salesTax);
    if (cfg.installationCost != null) setInstallationCost(cfg.installationCost);
    // Restore the freight quote and the inputs it was quoted from.
    setDestinationZip((cfg.destinationZip as string) || '');
    setLiftgate(!!cfg.liftgate);
    setResidential(!!cfg.residential);
    setFreightInfo((cfg.freightInfo as any) || null);
    setEditingQuoteId(savedQuote.id);
    setLoadedQuoteCreatedAt(savedQuote.createdAt || null);
    setQuoteName(savedQuote.name);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePushToOrder = (savedQuote: SavedQuote) => {
    // Warn on convert if the quote has passed its 30-day validity window.
    const validUntil = savedQuote.validUntil
      ? new Date(savedQuote.validUntil)
      : (() => { const d = new Date(savedQuote.createdAt); d.setDate(d.getDate() + 30); return d; })();
    const expired = savedQuote.isExpired ?? (Date.now() > validUntil.getTime());
    if (expired) {
      const ok = window.confirm(
        `This quote expired on ${validUntil.toLocaleDateString()}.\n\n` +
        `Quotes are valid for 30 days. Your order will be priced at CURRENT rates, ` +
        `which may differ from the quoted amount.\n\nContinue to the order form?`
      );
      if (!ok) return;
    }
    sessionStorage.setItem('quoteToOrder', JSON.stringify(savedQuote));
    navigate('/orders/new');
  };

  // Get freight quote from Freightview
  const handleGetFreightQuote = async () => {
    if (!destinationZip || destinationZip.length !== 5) {
      setFreightError('Please enter a valid 5-digit ZIP code');
      return;
    }

    const validScreens = screens.filter(s => s.width > 0 && s.height > 0);
    if (validScreens.length === 0) {
      setFreightError('Please add at least one screen with dimensions');
      return;
    }

    setFreightLoading(true);
    setFreightError('');
    setFreightInfo(null);

    try {
      // Each screen ships in its own box/skid (no pallets): 12"W × 14"H,
      // L = longest dim + 10". Weight = L(ft) × 14.5 lb/ft (BOL-calibrated).
      const boxes = validScreens.map(s => {
        const len = Math.round(Math.max(s.width, s.height) + 10);
        return { length: len, width: 12, height: 14, weight: Math.round((len / 12) * 14.5) };
      });
      const length = Math.round(Math.max(...boxes.map(b => b.length)));
      const height = 14;
      const weight = boxes.reduce((sum, b) => sum + b.weight, 0);
      const pallets = boxes.length;

      const result = await freightApi.estimate({
        destination_zip: destinationZip,
        length,
        width: 12,
        height,
        weight,
        pallets,
        boxes,
        freight_class: 85,
        accessorials: {
          liftgate,
          residential,
          inside: false,
        },
      });

      if (result.success) {
        setFreightInfo({
          carrier: result.carrier,
          service: result.service,
          transitTime: result.transitTime,
          rate: result.rate,
          fallback: result.fallback,
        });
      } else {
        setFreightError(result.error || 'Unable to get freight quote');
      }
    } catch (err: any) {
      console.error('Freight error:', err);
      setFreightError('Error getting freight quote. Please try again.');
    } finally {
      setFreightLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Quick Estimate Calculator"
        subtitle="Get instant pricing estimates for V-Track screens"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Configuration */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Type & Pricing Mode */}
          <div className="card">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Fabric <InfoTooltip text="Pick the actual fabric (Omega Pro 5%, 502 Vinyl, etc.). The Solar/Premium/Vinyl/Insect price matrix is set automatically by the fabric you pick." /></label>
                <select
                  value={fabricType}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    const fabric = FABRIC_FLAT.find(f => f.value === nextValue);
                    if (!fabric) return;
                    setFabricType(nextValue);
                    setProductType(fabric.pricingCategory);
                    const colors = COLORS_BY_FABRIC[nextValue] || ['Black'];
                    if (!colors.includes(fabricColor)) setFabricColor(colors[0]);
                  }}
                  className="input-field w-auto min-w-[20rem]"
                >
                  {FABRIC_TYPE_GROUPS.map(g => (
                    <optgroup key={g.group} label={g.group}>
                      {g.options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-gray-500 italic">
                  Pricing matrix: <span className="text-gray-400">{PRODUCT_LABELS[productType]}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Fabric Color</label>
                <select
                  value={fabricColor}
                  onChange={(e) => setFabricColor(e.target.value)}
                  className="input-field w-auto min-w-[14rem]"
                >
                  {(COLORS_BY_FABRIC[fabricType] || ['Black']).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {pricingDisplay === 'both' && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">View Mode</label>
                <div className="flex rounded-lg overflow-hidden border border-keder-gray">
                  <button
                    onClick={() => setPricingMode('dealer')}
                    className={clsx(
                      'px-4 py-2 text-sm font-medium transition-colors',
                      pricingMode === 'dealer'
                        ? 'bg-keder-yellow text-black'
                        : 'bg-keder-dark text-gray-300 hover:bg-keder-gray'
                    )}
                  >
                    Your Cost
                  </button>
                  <button
                    onClick={() => setPricingMode('msrp')}
                    className={clsx(
                      'px-4 py-2 text-sm font-medium transition-colors',
                      pricingMode === 'msrp'
                        ? 'bg-keder-yellow text-black'
                        : 'bg-keder-dark text-gray-300 hover:bg-keder-gray'
                    )}
                  >
                    Customer Price
                  </button>
                </div>
              </div>
              )}
            </div>

            {/* Screens List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Screens ({screens.length})</h3>
                <button
                  onClick={addScreen}
                  disabled={screens.length >= 100}
                  className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  + Add Screen
                </button>
              </div>

              <div className="flex items-center justify-end gap-2 mb-2">
                <span className="text-xs text-gray-500">Input mode:</span>
                <button
                  type="button"
                  onClick={() => setFractionMode(!fractionMode)}
                  className={clsx(
                    'px-3 py-1 text-xs font-semibold rounded-full transition-colors',
                    fractionMode
                      ? 'bg-keder-yellow text-black'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  )}
                >
                  {fractionMode ? '⅝ Fractions' : '.125 Decimal'}
                </button>
              </div>

              {screens.map((screen, index) => {
                const lineItem = quote.lineItems[index];
                return (
                  <div key={screen.id} className="border border-keder-gray rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white font-medium">Screen {index + 1}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => duplicateScreen(screen.id)}
                          disabled={screens.length >= 100}
                          className="text-gray-400 hover:text-white text-sm disabled:opacity-50"
                        >
                          Duplicate
                        </button>
                        <button
                          onClick={() => removeScreen(screen.id)}
                          disabled={screens.length <= 1}
                          className="text-red-400 hover:text-red-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Width (inches)</label>
                        <DimensionInput
                          value={screen.width ? String(screen.width) : ''}
                          fractionMode={fractionMode}
                          onChange={(val) => updateScreen(screen.id, { width: parseFloat(val) || 0 })}
                          min="1"
                          max="288"
                          placeholder={fractionMode ? 'e.g. 96 5/16' : 'Enter width'}
                          className="input-field w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Height (inches)</label>
                        <DimensionInput
                          value={screen.height ? String(screen.height) : ''}
                          fractionMode={fractionMode}
                          onChange={(val) => updateScreen(screen.id, { height: parseFloat(val) || 0 })}
                          min="1"
                          max="192"
                          placeholder={fractionMode ? 'e.g. 72 1/2' : 'Enter height'}
                          className="input-field w-full"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={screen.uChannel}
                            onChange={(e) => updateScreen(screen.id, { uChannel: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                          />
                          <span className="text-sm text-gray-300">U-Channel</span>
                        </label>
                      </div>
                      <div className="text-right">
                        <label className="block text-xs text-gray-400 mb-1">Unit Price</label>
                        {lineItem?.hasValidDimensions ? (
                          lineItem.isBelowMinWidth ? (
                            <span className="text-sm text-red-400">Not available</span>
                          ) : lineItem.isOverMaximum ? (
                            <span className="text-sm text-red-400">Custom Quote</span>
                          ) : (
                            <span className="text-lg font-semibold text-keder-yellow">
                              ${(pricingMode === 'msrp' ? lineItem.msrpPrice : lineItem.dealerPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          )
                        ) : (
                          <span className="text-gray-500">--</span>
                        )}
                      </div>
                    </div>

                    {/* Hard block: below the 30" buildable floor (mirrors New Order) */}
                    {lineItem?.isBelowMinWidth && (
                      <div className="mt-2 text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded">
                        Width must be at least 30". We don't build screens narrower than 30 inches.
                      </div>
                    )}

                    {/* Show note for undersized screens */}
                    {lineItem?.hasValidDimensions && lineItem.isUnderMinimum && !lineItem.isOverMaximum && !lineItem.isBelowMinWidth && (
                      <div className="mt-2 text-xs text-yellow-500 bg-yellow-500/10 px-2 py-1 rounded">
                        Screens under {MIN_WIDTH}"W or {MIN_HEIGHT}"H are priced at minimum dimensions ({MIN_WIDTH}" x {MIN_HEIGHT}")
                      </div>
                    )}

                    {/* Window panel row */}
                    <div className="mt-3 flex flex-wrap items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!screen.windowPanel}
                          onChange={(e) => updateScreen(screen.id, {
                            windowPanel: e.target.checked,
                            windowCount: e.target.checked ? Math.max(1, screen.windowCount || 1) : 1,
                          })}
                          className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                        />
                        <span className="text-sm text-gray-300">Vinyl Window Panel</span>
                      </label>
                      {screen.windowPanel && (
                        <>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-400">Windows</label>
                            <input
                              type="number"
                              min={1}
                              value={screen.windowCount || 1}
                              onChange={(e) => updateScreen(screen.id, { windowCount: Math.max(1, parseInt(e.target.value) || 1) })}
                              onWheel={(e) => e.currentTarget.blur()}
                              className="w-16 input-field text-sm text-center"
                            />
                          </div>
                          <span className="text-[11px] text-gray-500 italic">
                            First window included.
                            {Math.max(0, (screen.windowCount || 1) - 1) > 0 && (
                              <span className="text-gray-400 ml-1">
                                +{Math.max(0, (screen.windowCount || 1) - 1)} extra × ${EXTRA_WINDOW_PRICE} = ${(Math.max(0, (screen.windowCount || 1) - 1) * EXTRA_WINDOW_PRICE).toLocaleString()}
                              </span>
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Motor Package & Controls */}
          <div className="card">
            <h3 className="text-lg font-medium text-white mb-4">Motor Package & Controls</h3>

            {/* Motor Package Selection */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {MOTOR_PACKAGES.map(pkg => {
                const isSelected = motorPackage === pkg.value;
                return (
                  <button
                    key={pkg.value}
                    type="button"
                    onClick={() => {
                      setMotorPackage(pkg.value as any);
                      setRemoteType('none');
                      setWallSwitchType('none');
                      if (pkg.value === 'none') {
                        setRemoteQty(0);
                        setWallSwitchQty(0);
                      }
                    }}
                    className={`relative p-3 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? 'border-keder-yellow bg-keder-yellow/10'
                        : 'border-gray-600 hover:border-gray-500 bg-keder-darker'
                    }`}
                  >
                    {pkg.badge && (
                      <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                        {pkg.badge}
                      </span>
                    )}
                    <div className="font-medium text-white text-sm mb-0.5">{pkg.label}</div>
                    <div className="text-xs text-gray-400">{pkg.description}</div>
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <svg className="w-4 h-4 text-keder-yellow" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Somfy upgrade note */}
            {motorPackage === 'somfy' && (
              <div className="mb-4 p-3 bg-blue-900/30 border border-blue-500 rounded-lg text-sm text-blue-200">
                <strong>Somfy Upgrade:</strong> $275 per screen unit added automatically for Somfy motors.
              </div>
            )}

            {/* Remote & Wall Switch Controls */}
            {motorPackage !== 'none' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Remote Type</label>
                  <select
                    value={remoteType}
                    onChange={(e) => setRemoteType(e.target.value)}
                    className="input-field w-full"
                  >
                    {(motorPackage === 'alpha' ? ALPHA_REMOTES
                      : motorPackage === 'somfy' ? SOMFY_REMOTES
                      : KEDER_REMOTES
                    ).map(opt => (
                      <option key={opt.value} value={opt.value} disabled={(opt as any).disabled}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Remote QTY</label>
                  <input
                    type="number"
                    value={remoteQty}
                    onChange={(e) => setRemoteQty(parseInt(e.target.value) || 0)}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="input-field w-full"
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Wall Switch</label>
                  <select
                    value={wallSwitchType}
                    onChange={(e) => setWallSwitchType(e.target.value)}
                    className="input-field w-full"
                  >
                    {(motorPackage === 'alpha' ? ALPHA_WALL_SWITCHES
                      : motorPackage === 'somfy' ? SOMFY_WALL_SWITCHES
                      : KEDER_WALL_SWITCHES
                    ).map(opt => (
                      <option key={opt.value} value={opt.value} disabled={(opt as any).disabled}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Wall Switch QTY</label>
                  <input
                    type="number"
                    value={wallSwitchQty}
                    onChange={(e) => setWallSwitchQty(parseInt(e.target.value) || 0)}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="input-field w-full"
                    min={0}
                  />
                </div>
              </div>
            )}

            {/* Additional Options */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {pricingMode === 'msrp' && (
                <>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Markup %</label>
                    <input
                      type="number"
                      value={msrpMarkup}
                      onChange={(e) => setMsrpMarkup(parseFloat(e.target.value) || 0)}
                      onWheel={(e) => e.currentTarget.blur()}
                      min={0}
                      max={100}
                      step={0.5}
                      className="input-field w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Discount %</label>
                    <input
                      type="number"
                      value={msrpDiscount}
                      onChange={(e) => setMsrpDiscount(parseFloat(e.target.value) || 0)}
                      onWheel={(e) => e.currentTarget.blur()}
                      min={0}
                      max={50}
                      step={0.5}
                      className="input-field w-full"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs text-gray-400 mb-1">Sales Tax %</label>
                <input
                  type="number"
                  value={salesTax}
                  onChange={(e) => setSalesTax(parseFloat(e.target.value) || 0)}
                  onWheel={(e) => e.currentTarget.blur()}
                  min={0}
                  max={15}
                  step={0.25}
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Installation $/screen</label>
                <input
                  type="number"
                  value={installationCost}
                  onChange={(e) => setInstallationCost(parseFloat(e.target.value) || 0)}
                  onWheel={(e) => e.currentTarget.blur()}
                  min={0}
                  step={50}
                  className="input-field w-full"
                />
              </div>
            </div>
          </div>

          {/* Shipping Estimate Section */}
          <div className="card">
            <h3 className="text-lg font-medium text-white mb-4">Shipping Estimate</h3>
            <ShippingDisclaimer className="mb-4" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Destination ZIP Code</label>
                <input
                  type="text"
                  value={destinationZip}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setDestinationZip(value);
                    setFreightInfo(null);
                    setFreightError('');
                  }}
                  placeholder="Enter 5-digit ZIP"
                  maxLength={5}
                  className="input-field w-full"
                />
              </div>

              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={liftgate}
                    onChange={(e) => {
                      setLiftgate(e.target.checked);
                      setFreightInfo(null);
                    }}
                    className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                  />
                  <span className="text-sm text-gray-300">Liftgate</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={residential}
                    onChange={(e) => {
                      setResidential(e.target.checked);
                      setFreightInfo(null);
                    }}
                    className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                  />
                  <span className="text-sm text-gray-300">Residential</span>
                </label>
              </div>
            </div>

            <button
              onClick={handleGetFreightQuote}
              disabled={freightLoading || !destinationZip || destinationZip.length !== 5}
              className="btn-secondary w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {freightLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Getting Quote...
                </span>
              ) : (
                'Get Freight Quote'
              )}
            </button>

            {freightError && (
              <div className="mt-3 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {freightError}
              </div>
            )}

            {freightInfo && (
              <div className="mt-4 p-4 bg-green-900/30 border border-green-500/50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-white font-medium">{freightInfo.carrier}</p>
                    <p className="text-sm text-gray-400">{freightInfo.service}</p>
                    {freightInfo.transitTime && (
                      <p className="text-xs text-gray-500 mt-1">{freightInfo.transitTime}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-keder-yellow">${freightInfo.rate.toFixed(2)}</p>
                    {freightInfo.fallback && (
                      <p className="text-xs text-yellow-500">Estimated rate</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Origin: Lavon, TX 75166 | Freight Class: 85
                </p>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-3">
              Freight quotes are estimates. Final shipping cost will be confirmed when placing your order.
            </p>
          </div>

        </div>

        {/* Saved Estimates - order-3 on mobile so summary slots before it */}
        <div className="lg:col-span-2 space-y-6 order-3 lg:order-none">
          <div className="card">
            <h3 className="text-lg font-medium text-white mb-4">Saved Estimates</h3>

            {loadingQuotes ? (
              <div className="text-center py-8 text-gray-400">Loading saved estimates...</div>
            ) : savedQuotes.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No saved estimates yet. Calculate an estimate above and click "Save Estimate" to save it.
              </div>
            ) : (
              <div className="space-y-3">
                {savedQuotes.map((savedQuote) => {
                  const validUntil = savedQuote.validUntil
                    ? new Date(savedQuote.validUntil)
                    : (() => { const d = new Date(savedQuote.createdAt); d.setDate(d.getDate() + 30); return d; })();
                  const expired = savedQuote.isExpired ?? (Date.now() > validUntil.getTime());
                  return (
                  <div
                    key={savedQuote.id}
                    className={`border rounded-lg p-4 transition-colors ${expired ? 'border-red-500/40 bg-red-900/10' : 'border-keder-gray hover:border-keder-yellow/50'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-white font-medium">{savedQuote.name}</h4>
                        <p className="text-sm text-gray-400">
                          {savedQuote.configuration.screens.length} screen{savedQuote.configuration.screens.length !== 1 ? 's' : ''} |{' '}
                          {PRODUCT_LABELS_SHORT[savedQuote.configuration.productType] || savedQuote.configuration.productType} |{' '}
                          {new Date(savedQuote.createdAt).toLocaleDateString()}
                        </p>
                        {expired ? (
                          <p className="text-xs text-red-400 font-semibold mt-1">
                            EXPIRED {validUntil.toLocaleDateString()} — pricing no longer valid
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500 mt-1">
                            Valid through {validUntil.toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-keder-yellow font-bold text-lg">
                          {/* All-in figure. grandTotalDealer is product-only (the
                              detail view adds freight on top), so the list used to
                              show a smaller number than the estimate itself once
                              shipping was quoted. Falls back for estimates saved
                              before freight was persisted. */}
                          ${(((savedQuote.pricing as any).grandTotalWithFreightDealer
                              ?? savedQuote.pricing.grandTotalDealer) as number)
                              .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        {((savedQuote.pricing as any).freightRate ?? 0) > 0 && (
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            incl. ${((savedQuote.pricing as any).freightRate as number).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} shipping
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleLoadQuote(savedQuote)}
                        className="text-sm px-3 py-1 border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 rounded transition-colors"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handlePushToOrder(savedQuote)}
                        className="text-sm px-3 py-1 bg-keder-yellow text-black font-medium rounded hover:bg-yellow-400 transition-colors"
                      >
                        Push to Order
                      </button>
                      <button
                        onClick={() => handleDeleteQuote(savedQuote.id)}
                        className="text-sm px-3 py-1 text-red-400 hover:text-red-300 transition-colors ml-auto"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Quote Summary (order-2 on mobile = after shipping, before saved estimates) */}
        <div className="order-2 lg:order-none space-y-6">
          <div className="card lg:sticky lg:top-8">
            <h3 className="text-lg font-medium text-white mb-4">Estimate Summary</h3>

            {screens.length === 0 || !screens.some(s => s.width > 0 && s.height > 0) ? (
              <div className="text-center py-8 text-gray-400">
                Enter screen dimensions to see pricing
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                {/* Line Items */}
                <div className="border-b border-keder-gray pb-3">
                  {quote.lineItems.filter(item => item.hasValidDimensions).map((item) => (
                    <div key={item.number} className="flex justify-between py-1">
                      <span className="text-gray-400">
                        Screen {item.number} ({item.width}" x {item.height}")
                        {item.hasUChannel && ' + U-Ch'}
                        {item.isUnderMinimum && ' *'}
                      </span>
                      <span className="text-white">
                        {item.isOverMaximum
                          ? 'Custom'
                          : `$${(pricingMode === 'msrp' ? item.msrpPrice : item.dealerPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        }
                      </span>
                    </div>
                  ))}
                </div>

                {/* Subtotals */}
                <div className="flex justify-between">
                  <span className="text-gray-400">Screens Subtotal</span>
                  <span className="text-white">
                    ${(pricingMode === 'msrp' ? quote.screensMSRP : quote.screensDealer).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                {quote.uChannelFeet > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">U-Channel ({quote.uChannelFeet.toFixed(1)} ft @ ${pricingMode === 'msrp' ? '12.75' : '9.16'}/ft)</span>
                    <span className="text-white">
                      ${(pricingMode === 'msrp' ? quote.uChannelMSRP : quote.uChannelDealer).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}

                {quote.extraWindowsCount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Extra Vinyl Windows ({quote.extraWindowsCount} × ${EXTRA_WINDOW_PRICE})</span>
                    <span className="text-white">
                      ${(pricingMode === 'msrp' ? quote.extraWindowsMSRP : quote.extraWindowsDealer).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}

                {quote.remoteMSRP > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      {remoteType !== 'none'
                        ? `${(motorPackage === 'alpha' ? ALPHA_REMOTES : motorPackage === 'somfy' ? SOMFY_REMOTES : KEDER_REMOTES).find(r => r.value === remoteType)?.label || 'Remote'} x${remoteQty}`
                        : `Remote${remoteQty > 1 ? ` x${remoteQty}` : ''}`
                      }
                    </span>
                    <span className="text-white">
                      ${(pricingMode === 'msrp' ? quote.remoteMSRP : quote.remoteDealer).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}

                {quote.wallSwitchTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      {wallSwitchType !== 'none'
                        ? `${(motorPackage === 'alpha' ? ALPHA_WALL_SWITCHES : motorPackage === 'somfy' ? SOMFY_WALL_SWITCHES : KEDER_WALL_SWITCHES).find(w => w.value === wallSwitchType)?.label || 'Wall Switch'} x${wallSwitchQty}`
                        : `Wall Switch x${wallSwitchQty}`
                      }
                    </span>
                    <span className="text-white">
                      ${quote.wallSwitchTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}

                {quote.somfyUpgrade > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Somfy Upgrade ($275 x {screens.filter(s => s.width > 0 && s.height > 0).length})</span>
                    <span className="text-white">
                      ${quote.somfyUpgrade.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}

                {quote.alphaUpgrade > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Alpha Upgrade ($125 x {screens.filter(s => s.width > 0 && s.height > 0).length})</span>
                    <span className="text-white">
                      ${quote.alphaUpgrade.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}

                <div className="flex justify-between border-t border-keder-gray pt-3">
                  <span className="text-gray-400">Product Subtotal</span>
                  <span className="text-white font-medium">
                    ${(pricingMode === 'msrp' ? quote.markedUpMSRP : quote.subtotalDealer).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                {pricingMode === 'msrp' && quote.msrpDiscountAmount > 0 && (
                  <div className="flex justify-between text-red-400">
                    <span>- Discount ({msrpDiscount}%)</span>
                    <span>-${quote.msrpDiscountAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}

                {quote.packaging > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Packaging</span>
                    <span className="text-white">${quote.packaging.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}

                {quote.salesTaxAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Sales Tax ({salesTax}%)</span>
                    <span className="text-white">${quote.salesTaxAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}

                {quote.installationTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Installation</span>
                    <span className="text-white">${quote.installationTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}

                {/* Shipping */}
                {freightInfo && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Shipping ({freightInfo.carrier})</span>
                    <span className="text-white">${freightInfo.rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}

                {/* Sales tax preview */}
                {taxPreview > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      Sales Tax {taxState && <span className="text-gray-500 text-xs">({taxState} · {taxRatePct.toFixed(2)}%{taxStateSource === 'dealer_home' ? ' · est. home state' : ''})</span>}
                    </span>
                    <span className="text-white">${taxPreview.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}

                {/* Grand Total */}
                <div className="flex justify-between border-t border-keder-gray pt-3">
                  <span className="text-lg font-semibold text-white">
                    {pricingMode === 'msrp' ? 'Customer Total' : 'Your Cost'}
                  </span>
                  <span className="text-2xl font-bold text-keder-yellow">
                    ${((pricingMode === 'msrp' ? quote.grandTotalMSRP : quote.grandTotalDealer) + (freightInfo?.rate || 0) + taxPreview).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <p className="text-xs text-gray-500 italic text-center mt-2">
                  Estimate only. Final pricing may vary.
                </p>

                {/* Profit (only in dealer cost mode — never show when viewing customer price) */}
                {pricingMode === 'dealer' && pricingDisplay === 'both' && (
                  <div className="flex justify-between bg-green-900/30 rounded-lg p-3 mt-4">
                    <span className="text-green-400 font-medium">Estimated Profit</span>
                    <span className="text-green-400 font-bold text-lg">
                      ${quote.profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Hard block banner: a sub-30" screen is entered (mirrors New Order) */}
            {belowMinWidthError && (
              <div className="mt-4 text-sm text-red-300 bg-red-500/10 border border-red-500/40 px-3 py-2 rounded-lg">
                {belowMinWidthError}
              </div>
            )}

            {/* Action Buttons */}
            {hasValidScreens && (
              <div className="mt-4 space-y-2">
                <button
                  onClick={() => setShowSaveModal(true)}
                  disabled={!!belowMinWidthError}
                  className="w-full py-3 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Estimate
                </button>

                <button
                  onClick={() => {
                    // Print/PDF via a hidden iframe. (Removed a dead guard:
                    // getElementById('estimate-print-view') — that element never
                    // existed, so the whole handler silently no-op'd every time.)
                    {
                      // window.open('', '_blank') is
                      // popup-blocked by browsers — when it was, the handle was
                      // null and this silently did nothing ("print doesn't work").
                      const printFrame = document.createElement('iframe');
                      printFrame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
                      document.body.appendChild(printFrame);
                      const printWindow = printFrame.contentWindow;
                      if (printWindow) {
                        const logoUrl = (user as any)?.logoUrl || 'https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png';
                        const companyName = (user as any)?.dealerName || (user as any)?.companyName || 'Keder Screens';
                        printWindow.document.write(`
                          <html><head><title>Estimate - ${companyName}</title>
                          <style>
                            body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
                            .header { display: flex; align-items: center; gap: 20px; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #e5e7eb; }
                            .header img { max-height: 50px; }
                            .header h1 { font-size: 24px; margin: 0; }
                            .header p { margin: 4px 0 0 0; color: #666; }
                            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                            th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
                            th { background: #f5f5f5; font-size: 12px; text-transform: uppercase; color: #666; }
                            .total-row { font-weight: bold; font-size: 18px; border-top: 2px solid #333; }
                            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #999; }
                            @media print { body { padding: 20px; } }
                          </style></head><body>
                          <div class="header">
                            <img src="${logoUrl}" alt="${companyName}" />
                            <div>
                              <h1>${companyName}</h1>
                              <p>Estimate - ${new Date().toLocaleDateString()}</p>
                            </div>
                          </div>
                          <table>
                            <thead><tr><th>#</th><th>Dimensions</th><th>Product</th><th style="text-align:right">Price</th></tr></thead>
                            <tbody>
                              ${quote.lineItems.filter(i => i.hasValidDimensions).map(i => `
                                <tr>
                                  <td>${i.number}</td>
                                  <td>${i.width}" x ${i.height}"</td>
                                  <td>${productType === 'cvw' ? 'Clear Vinyl' : productType === 'solar99' ? 'Premium Solar' : productType === 'solar94' ? 'Solar' : 'Insect'}</td>
                                  <td style="text-align:right">$${(pricingMode === 'msrp' ? i.msrpPrice * quote.displayMarkup : i.dealerPrice).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                              `).join('')}
                              ${(quote.uChannelFeet > 0 || quote.extraWindowsCount > 0 || quote.remoteMSRP > 0 || quote.wallSwitchTotal > 0 || quote.somfyUpgrade > 0 || quote.alphaUpgrade > 0 || (pricingMode === 'msrp' && quote.msrpDiscountAmount > 0) || (pricingMode === 'msrp' && quote.salesTaxAmount > 0) || quote.installationTotal > 0 || freightInfo?.rate) ? `
                              <tr style="background:#fafafa"><td colspan="3" style="font-weight:600">Product Subtotal</td><td style="text-align:right;font-weight:600">$${(pricingMode === 'msrp' ? quote.displayScreenTotal : quote.subtotalDealer).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
                              ${quote.uChannelFeet > 0 ? `<tr><td colspan="3" style="color:#555">U-Channel (${quote.uChannelFeet.toFixed(1)} ft @ $${pricingMode === 'msrp' ? '12.75' : '9.16'}/ft)</td><td style="text-align:right">$${(pricingMode === 'msrp' ? quote.uChannelMSRP * quote.displayMarkup : quote.uChannelDealer).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ${quote.extraWindowsCount > 0 ? `<tr><td colspan="3" style="color:#555">Extra Vinyl Windows (${quote.extraWindowsCount} × $${EXTRA_WINDOW_PRICE})</td><td style="text-align:right">$${(pricingMode === 'msrp' ? quote.extraWindowsMSRP * quote.displayMarkup : quote.extraWindowsDealer).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ${quote.remoteMSRP > 0 ? `<tr><td colspan="3" style="color:#555">Remotes</td><td style="text-align:right">$${(pricingMode === 'msrp' ? quote.remoteMSRP * quote.displayMarkup : quote.remoteDealer).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ${quote.wallSwitchTotal > 0 ? `<tr><td colspan="3" style="color:#555">Wall Switches</td><td style="text-align:right">$${(pricingMode === 'msrp' ? quote.wallSwitchTotal * quote.displayMarkup : quote.wallSwitchTotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ${quote.somfyUpgrade > 0 ? `<tr><td colspan="3" style="color:#555">Somfy Upgrade</td><td style="text-align:right">$${(pricingMode === 'msrp' ? quote.somfyUpgrade * quote.displayMarkup : quote.somfyUpgrade).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ${quote.alphaUpgrade > 0 ? `<tr><td colspan="3" style="color:#555">Alpha Upgrade</td><td style="text-align:right">$${(pricingMode === 'msrp' ? quote.alphaUpgrade * quote.displayMarkup : quote.alphaUpgrade).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ${''}<!-- Markup is baked into every price above; never itemised -->
                              ${pricingMode === 'msrp' && quote.msrpDiscountAmount > 0 ? `<tr><td colspan="3" style="color:#555">Discount (${msrpDiscount}%)</td><td style="text-align:right">-$${quote.msrpDiscountAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ${quote.packaging > 0 ? `<tr><td colspan="3" style="color:#555">Packaging</td><td style="text-align:right">$${quote.packaging.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ${pricingMode === 'msrp' && quote.salesTaxAmount > 0 ? `<tr><td colspan="3" style="color:#555">Sales Tax (${salesTax}%)</td><td style="text-align:right">$${quote.salesTaxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ${quote.installationTotal > 0 ? `<tr><td colspan="3" style="color:#555">Installation</td><td style="text-align:right">$${quote.installationTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ${freightInfo?.rate ? `<tr><td colspan="3" style="color:#555">Shipping (${freightInfo.carrier})</td><td style="text-align:right">$${freightInfo.rate.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>` : ''}
                              ` : ''}
                              <tr class="total-row">
                                <td colspan="3">Estimated Total</td>
                                <td style="text-align:right">$${((pricingMode === 'msrp' ? quote.grandTotalMSRP : quote.grandTotalDealer) + (freightInfo?.rate || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                              </tr>
                            </tbody>
                          </table>
                          <p style="background:#fff7ed; border:1px solid #fb923c; padding:10px 14px; border-radius:6px; color:#9a3412;">
                            <strong>This estimate is valid for 30 days &mdash; through ${(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); })()}.</strong>
                            Orders placed after this date are subject to current pricing. Prices may also vary based on final measurements.
                          </p>
                          <div class="footer">
                            ${(user as any)?.logoUrl ? `${companyName} | Powered by Keder Screens` : 'Keder Screens | 12446 State Hwy 205, Lavon, TX 75166'}
                          </div>
                          </body></html>
                        `);
                        printWindow.document.close();
                        const doPrint = () => {
                          try { printWindow.focus(); printWindow.print(); }
                          finally { setTimeout(() => { try { document.body.removeChild(printFrame); } catch { /* already removed */ } }, 1000); }
                        };
                        // Wait for the logo so it isn't missing from the PDF.
                        const logoImg = printWindow.document.querySelector('img');
                        if (logoImg && !logoImg.complete) {
                          logoImg.addEventListener('load', () => setTimeout(doPrint, 100));
                          logoImg.addEventListener('error', () => setTimeout(doPrint, 100));
                          setTimeout(doPrint, 1500);
                        } else {
                          setTimeout(doPrint, 200);
                        }
                      }
                    }
                  }}
                  className="w-full py-3 border border-gray-600 text-gray-300 font-medium rounded-lg hover:bg-keder-gray/50 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print / Save PDF
                </button>

                <button
                  onClick={() => setShowEmailModal(true)}
                  className="w-full py-3 border border-blue-500 text-blue-400 font-medium rounded-lg hover:bg-blue-500/10 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email Estimate to Customer
                </button>
              </div>
            )}

            {/* Preview in Visualizer Link */}
            {hasValidScreens && (
              <Link
                to="/designer"
                className="w-full mt-2 py-3 border border-keder-yellow text-keder-yellow font-semibold rounded-lg hover:bg-keder-yellow/10 transition-colors block text-center"
              >
                Preview in Visualizer
              </Link>
            )}

            {/* Dimension Info Note */}
            <div className="mt-6 p-3 bg-keder-gray/30 rounded-lg">
              <p className="text-xs text-gray-400">
                <strong>Pricing Notes:</strong><br />
                Standard: {MIN_WIDTH}" - {MAX_WIDTH}"W | {MIN_HEIGHT}" - {MAX_HEIGHT}"H<br />
                <span className="text-yellow-500">* Screens smaller than {MIN_WIDTH}"W or {MIN_HEIGHT}"H are priced at minimum dimensions</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Save Estimate Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-keder-dark border border-keder-gray rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-white mb-4">
              {editingQuoteId ? 'Update Estimate' : 'Save Estimate'}
            </h3>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Estimate Name</label>
              <input
                type="text"
                value={quoteName}
                onChange={(e) => setQuoteName(e.target.value)}
                placeholder="e.g., Smith Residence Patio"
                className="input-field w-full"
                autoFocus
              />
            </div>

            {saveError && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {saveError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setQuoteName('');
                  setSaveError(null);
                }}
                className="flex-1 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-keder-gray transition-colors"
              >
                Cancel
              </button>
              {editingQuoteId && (
                <button
                  onClick={() => handleSaveQuote(false)}
                  disabled={saving}
                  className="flex-1 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-500 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Updating...' : 'Update'}
                </button>
              )}
              <button
                onClick={() => handleSaveQuote(true)}
                disabled={saving}
                className="flex-1 py-2 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingQuoteId ? 'Save as New' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Estimate Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-keder-dark border border-keder-gray rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Email Estimate to Customer</h3>

            {emailSent ? (
              <div className="text-center py-6">
                <svg className="w-16 h-16 text-green-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-white text-lg mb-2">Estimate Sent!</p>
                <p className="text-gray-400 text-sm mb-4">Your branded estimate has been emailed to {emailTo}</p>
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailSent(false);
                    setEmailTo('');
                    setEmailName('');
                    setEmailNotes('');
                    setEmailPreviewImages([]);
                  }}
                  className="px-6 py-2 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Customer Name *</label>
                    <input
                      type="text"
                      value={emailName}
                      onChange={(e) => setEmailName(e.target.value)}
                      placeholder="John Smith"
                      className="input-field w-full"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Customer Email *</label>
                    <input
                      type="email"
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      placeholder="customer@example.com"
                      className="input-field w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Notes (optional)</label>
                    <textarea
                      value={emailNotes}
                      onChange={(e) => setEmailNotes(e.target.value)}
                      placeholder="Any additional notes for the customer..."
                      className="input-field w-full h-20 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      Screen Preview Images (optional){emailPreviewImages.length > 0 && ` — ${emailPreviewImages.length} attached`}
                    </label>
                    <input
                      ref={emailImageRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (!files) return;
                        Array.from(files).forEach(file => {
                          if (file.size > 5 * 1024 * 1024) return;
                          const reader = new FileReader();
                          reader.onload = () => setEmailPreviewImages(prev => [...prev, reader.result as string]);
                          reader.readAsDataURL(file);
                        });
                        setShowDesignPicker(false);
                        if (emailImageRef.current) emailImageRef.current.value = '';
                      }}
                    />
                    {/* Attached images */}
                    {emailPreviewImages.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        {emailPreviewImages.map((img, i) => (
                          <div key={i} className="relative">
                            <img src={img} alt={`Preview ${i + 1}`} className="w-full h-20 object-cover rounded-lg bg-black/30 border border-keder-gray" />
                            <button
                              onClick={() => setEmailPreviewImages(prev => prev.filter((_, j) => j !== i))}
                              className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 text-white rounded-full text-xs font-bold flex items-center justify-center hover:bg-red-500"
                            >X</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Add more buttons */}
                    {showDesignPicker ? (
                      <div className="border border-keder-gray rounded-lg bg-keder-dark">
                        {emailDesigns.length === 0 ? (
                          <p className="text-gray-500 text-xs p-3">No saved designs found. <button onClick={() => setShowDesignPicker(false)} className="text-blue-400 hover:underline">Upload photos instead</button></p>
                        ) : (
                          <>
                            <div className="max-h-40 overflow-y-auto">
                              {emailDesigns.map((d: any) => (
                                <button
                                  key={d.id}
                                  type="button"
                                  onClick={() => {
                                    if (d.image_url) setEmailPreviewImages(prev => prev.includes(d.image_url) ? prev : [...prev, d.image_url]);
                                  }}
                                  className={`w-full flex items-center gap-2 p-2 hover:bg-keder-gray/50 transition-colors text-left ${emailPreviewImages.includes(d.image_url) ? 'bg-green-900/20 border-l-2 border-green-500' : ''}`}
                                >
                                  {d.image_url && <img src={d.image_url} alt="" className="w-12 h-8 object-cover rounded flex-shrink-0" />}
                                  <div className="min-w-0 flex-1">
                                    <p className="text-white text-xs truncate">{d.name}</p>
                                    <p className="text-gray-500 text-xs">{d.screen_count} screen{d.screen_count !== 1 ? 's' : ''}</p>
                                  </div>
                                  {emailPreviewImages.includes(d.image_url) && <span className="text-green-400 text-xs">Added</span>}
                                </button>
                              ))}
                            </div>
                            <button onClick={() => setShowDesignPicker(false)} className="w-full text-center text-gray-500 text-xs py-1.5 border-t border-keder-gray hover:text-white">Done</button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const token = localStorage.getItem('auth_token');
                            const apiUrl = import.meta.env.VITE_API_URL || '/api';
                            fetch(`${apiUrl}/screen-designs`, { headers: { Authorization: `Bearer ${token}` } })
                              .then(r => r.ok ? r.json() : [])
                              .then(designs => { setEmailDesigns(designs); setShowDesignPicker(true); })
                              .catch(() => setShowDesignPicker(true));
                          }}
                          className="flex-1 py-3 border border-dashed border-blue-500/50 rounded-lg text-blue-400 text-sm hover:border-blue-400 hover:text-blue-300 transition-colors"
                        >
                          From Saved Designs
                        </button>
                        <button
                          type="button"
                          onClick={() => emailImageRef.current?.click()}
                          className="flex-1 py-3 border border-dashed border-keder-gray rounded-lg text-gray-400 text-sm hover:border-keder-yellow hover:text-white transition-colors"
                        >
                          Upload Photos
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {(user as any)?.logoUrl && (
                  <div className="mb-4 p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg text-sm text-purple-300">
                    This estimate will be branded with your company logo and sent from {(user as any)?.brandedEmail || 'Keder Screens'}.
                  </div>
                )}

                {emailError && (
                  <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-400 text-sm">
                    {emailError}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmailError(null);
                    }}
                    className="flex-1 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-keder-gray transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!emailTo || !emailName) {
                        setEmailError('Please enter customer name and email');
                        return;
                      }
                      if (belowMinWidthError) {
                        setEmailError(belowMinWidthError);
                        return;
                      }
                      try {
                        setEmailSending(true);
                        setEmailError(null);
                        // Build extras (accessories, tax, installation, shipping)
                        const extras: { label: string; amount: number }[] = [];
                        // ALWAYS customer-facing prices on customer emails — never
                        // leak the dealer's cost view even if the dealer is currently
                        // looking at "Your Cost" while drafting.
                        const isMsrp = true;
                        // U-Channel — was missing, so the emailed line items didn't
                        // reconcile to the subtotal (its cost sat hidden inside it).
                        if (quote.uChannelFeet > 0) {
                          extras.push({
                            label: `U-Channel (${quote.uChannelFeet.toFixed(1)} ft @ $${isMsrp ? '12.75' : '9.16'}/ft)`,
                            amount: (isMsrp ? quote.uChannelMSRP : quote.uChannelDealer) * quote.displayMarkup,
                          });
                        }
                        if (quote.extraWindowsCount > 0) {
                          extras.push({
                            label: `Extra Vinyl Windows (${quote.extraWindowsCount} × $${EXTRA_WINDOW_PRICE})`,
                            amount: (isMsrp ? quote.extraWindowsMSRP : quote.extraWindowsDealer) * quote.displayMarkup,
                          });
                        }
                        if (quote.remoteMSRP > 0) {
                          const remoteLabel = remoteType !== 'none'
                            ? `${(motorPackage === 'alpha' ? ALPHA_REMOTES : motorPackage === 'somfy' ? SOMFY_REMOTES : KEDER_REMOTES).find(r => r.value === remoteType)?.label || 'Remote'} x${remoteQty}`
                            : `Remote x${remoteQty}`;
                          extras.push({ label: remoteLabel, amount: (isMsrp ? quote.remoteMSRP : quote.remoteDealer) * quote.displayMarkup });
                        }
                        if (quote.wallSwitchTotal > 0) {
                          const wsLabel = wallSwitchType !== 'none'
                            ? `${(motorPackage === 'alpha' ? ALPHA_WALL_SWITCHES : motorPackage === 'somfy' ? SOMFY_WALL_SWITCHES : KEDER_WALL_SWITCHES).find(w => w.value === wallSwitchType)?.label || 'Wall Switch'} x${wallSwitchQty}`
                            : `Wall Switch x${wallSwitchQty}`;
                          extras.push({ label: wsLabel, amount: quote.wallSwitchTotal * quote.displayMarkup });
                        }
                        if (quote.somfyUpgrade > 0) extras.push({ label: `Somfy Upgrade x${screens.filter(s => s.width > 0 && s.height > 0).length}`, amount: quote.somfyUpgrade * quote.displayMarkup });
                        if (quote.alphaUpgrade > 0) extras.push({ label: `Alpha Upgrade x${screens.filter(s => s.width > 0 && s.height > 0).length}`, amount: quote.alphaUpgrade * quote.displayMarkup });
                        // Markup is baked into screen prices — don't show % to customer
                        if (isMsrp && quote.msrpDiscountAmount > 0) extras.push({ label: `Discount (${msrpDiscount}%)`, amount: -quote.msrpDiscountAmount });
                        if (quote.packaging > 0) extras.push({ label: 'Packaging', amount: quote.packaging });
                        if (isMsrp && quote.salesTaxAmount > 0) extras.push({ label: `Sales Tax (${salesTax}%)`, amount: quote.salesTaxAmount });
                        if (quote.installationTotal > 0) extras.push({ label: 'Installation', amount: quote.installationTotal });
                        if (freightInfo?.rate) extras.push({ label: `Shipping (${freightInfo.carrier})`, amount: freightInfo.rate });

                        await quotesApi.sendEstimate({
                          customerEmail: emailTo,
                          customerName: emailName,
                          screens: quote.lineItems
                            .filter(i => i.hasValidDimensions)
                            .map(i => ({
                              index: i.number,
                              width: i.width,
                              height: i.height,
                              productType: productType === 'cvw' ? 'Clear Vinyl' : productType === 'solar99' ? 'Premium Solar' : productType === 'solar94' ? 'Solar' : 'Insect',
                              // ALWAYS customer-facing MSRP on emails to customers
                              // Markup baked in, never itemised — see displayMarkup.
                              msrpPrice: i.msrpPrice * quote.displayMarkup,
                            })),
                          // Customer total = MSRP grand total + shipping. Never dealer cost.
                          totalMsrp: quote.grandTotalMSRP + (freightInfo?.rate || 0),
                          notes: emailNotes || undefined,
                          extras: extras.length > 0 ? extras : undefined,
                          // Screens only. Accessories are their own rows below,
                          // so including them here counted them twice.
                          subtotal: extras.length > 0 ? quote.displayScreenTotal : undefined,
                          previewImages: emailPreviewImages.length > 0 ? emailPreviewImages : undefined,
                        });
                        setEmailSent(true);
                      } catch (err: any) {
                        setEmailError(err?.response?.data?.message || 'Failed to send estimate');
                      } finally {
                        setEmailSending(false);
                      }
                    }}
                    disabled={emailSending}
                    className="flex-1 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {emailSending ? 'Sending...' : 'Send Estimate'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default QuoteCalculatorPage;
